import React from "react";
import { Select, Space, Tag, Typography } from "antd";
import { TeamOutlined } from "@ant-design/icons";
import { useCohortWorkspace } from "../../../features/cohort-workspace/CohortWorkspaceContext";

const { Text } = Typography;

const STATUS_META: Record<string, { label: string; color: string }> = {
  accepting_applications: { label: "Applications", color: "green" },
  active: { label: "Active", color: "blue" },
  completed: { label: "Completed", color: "default" },
  archived: { label: "Archived", color: "default" },
  draft: { label: "Draft", color: "gold" },
  paused: { label: "Paused", color: "orange" },
};

function getStatusMeta(status: string) {
  return STATUS_META[status] || {
    label: status.replaceAll("_", " "),
    color: "default",
  };
}

interface CohortSelectorProps {
  compact?: boolean;
  className?: string;
  onChange?: (cohortId: string) => void;
}

export const CohortSelector: React.FC<CohortSelectorProps> = ({
  compact = false,
  className = "",
  onChange,
}) => {
  const { cohorts, cohortId, setCohortId, loading } = useCohortWorkspace();
  const options = cohorts.map((cohort) => {
    const status = getStatusMeta(cohort.status);
    const numberLabel = Number.isFinite(cohort.cohortNumber) ? `C${cohort.cohortNumber}` : cohort.name;

    return {
      value: cohort._id,
      label: `${numberLabel} - ${status.label}`,
      searchText: `${cohort.name} ${cohort.code} ${status.label}`,
      cohort,
      status,
    };
  });

  return (
    <div className={`cohort-selector ${compact ? "cohort-selector--compact" : ""} ${className}`.trim()}>
      {!compact && <TeamOutlined aria-hidden />}
      {!compact && <Text type="secondary">Cohort</Text>}
      <Select
        aria-label="Active cohort"
        value={cohortId || undefined}
        onChange={(nextCohortId) => {
          setCohortId(nextCohortId);
          onChange?.(nextCohortId);
        }}
        loading={loading}
        placeholder="Select cohort"
        showSearch
        optionFilterProp="searchText"
        popupMatchSelectWidth={280}
        className="cohort-selector__control"
        optionLabelProp="label"
        options={options}
        optionRender={(option) => {
          const data = option.data as (typeof options)[number];
          return (
            <Space style={{ justifyContent: "space-between", width: "100%" }} align="center">
              <span>{data.cohort.name}</span>
              <Tag bordered={false} color={data.status.color} style={{ marginInlineEnd: 0 }}>
                {data.status.label}
              </Tag>
            </Space>
          );
        }}
      />
    </div>
  );
};
